import { ISODate } from '@framework/ui/components/date/ISODate.tsx';
import { type ColumnsType } from 'antd/es/table';
import { type AlboRecordBoDTO } from '../backoffice/api/schemas/alboRecordBoDTO.ts';
import { StateTag } from '../component/StateTag.tsx';
import { type AlboConfigurazioneTipoDTO as AlboConfigurazioneTipoDTOBo } from '../configuration/api/schemas/alboConfigurazioneTipoDTO.ts';
import { EnumAlboConfigurazioneTipoCampoDTOTipo } from '../configuration/api/schemas/enumAlboConfigurazioneTipoCampoDTOTipo.ts';
import { type AlboConfigurazioneTipoDTO as AlboConfigurazioneTipoDTOFo } from '../frontoffice/api/schemas/alboConfigurazioneTipoDTO.ts';
import { type AlboRecordFoDTO } from '../frontoffice/api/schemas/alboRecordFoDTO.ts';
import { type RegisterRecordFieldVisibility } from '../schema/RegisterRecordFieldVisibility.ts';
import { sortRegisterRecordsTypeField } from './sortRegisterRecordsTypeField.ts';
/**
 * Crea le colonne per la tabella degli Elenchi in base alla configurazione del tipo di elenco.
 */

export function getRegisterRecordTypeColumns(
  registerRecordType:
    | AlboConfigurazioneTipoDTOBo
    | AlboConfigurazioneTipoDTOFo
    | null,
  registerRecordFieldVisibility?: RegisterRecordFieldVisibility,
) {
  if (!registerRecordType?.configurazioneCampi) return [];
  const configurationFields = registerRecordType.configurazioneCampi;

  const sortedFields = sortRegisterRecordsTypeField(configurationFields);

  const filteredSortedFields = registerRecordFieldVisibility
    ? sortedFields.filter(
        field => field[registerRecordFieldVisibility] && field.codice,
      )
    : sortedFields;

  /**
   * Colonne dinamiche basate sulla configurazione dei campi dell'albo
   */
  const dynamicColumns: ColumnsType<AlboRecordBoDTO | AlboRecordFoDTO> =
    filteredSortedFields.map(field => ({
      title: field.etichetta,
      dataIndex: ['valori', field.codice!],
      key: field.codice!,
      sorter: true,
      sortDataIndex: field.codice!,
      render: (value: string) => {
        if (field.tipo === EnumAlboConfigurazioneTipoCampoDTOTipo.DATE) {
          return <ISODate date={value} />;
        }
        return value;
      },
    }));

  /**
   * Colonna Stato (fissa)
   */
  const stateColumn: ColumnsType<AlboRecordBoDTO | AlboRecordFoDTO>[0] = {
    title: 'Stato',
    dataIndex: 'statoIscrizione',
    key: 'statoIscrizione',
    render: (
      statoIscrizione:
        | AlboRecordBoDTO['statoIscrizione']
        | AlboRecordFoDTO['statoIscrizione'],
    ) => {
      if (!statoIscrizione) return null;
      return (
        <StateTag
          state={statoIscrizione.codice ?? ''}
          color={statoIscrizione.colore ?? ''}
          icon={statoIscrizione.icona ?? ''}
        />
      );
    },
  };

  const numberColumn: ColumnsType<AlboRecordBoDTO | AlboRecordFoDTO>[0] = {
    title: 'Numero',
    dataIndex: 'numero',
    key: 'numero',
    sorter: true,
    sortDataIndex: 'numero',
  };

  const datesColumn: ColumnsType<AlboRecordBoDTO | AlboRecordFoDTO> = [
    {
      title: 'Data inizio',
      dataIndex: 'dataEmissione',
      key: 'dataEmissione',
      sorter: true,
      sortDataIndex: 'dataEmissione',
      render: (
        dataEmissione:
          | AlboRecordBoDTO['dataEmissione']
          | AlboRecordFoDTO['dataEmissione'],
      ) => {
        if (!dataEmissione) return null;
        return <ISODate date={dataEmissione} />;
      },
    },
    {
      title: 'Data fine',
      dataIndex: 'dataScadenza',
      key: 'dataScadenza',
      sorter: true,
      sortDataIndex: 'dataScadenza',
      render: (
        dataScadenza:
          | AlboRecordBoDTO['dataScadenza']
          | AlboRecordFoDTO['dataScadenza'],
      ) => {
        if (!dataScadenza) return null;
        return <ISODate date={dataScadenza} />;
      },
    },
  ];
  return [stateColumn, ...dynamicColumns, numberColumn, ...datesColumn];
}
